import { lazy, useEffect } from 'react';
import {
	HashRouter as Router,
	Routes as ReactRouterRoutes,
	Route,
} from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import RequirePermission from './components/RequirePermission';

// Lazy load all pages
const ErrorPage = lazy(() => import('./pages/ErrorPage'));
const NotFound = lazy(() => import('./pages/NotFoundPage'));
const Alerts = lazy(() => import('./pages/alerts/Alerts'));
const AlertsDetail = lazy(() => import('./pages/alerts/AlertDetail'));
const Subscriptions = lazy(() => import('./pages/subscriptions/Subscriptions'));
const RepositoryDetail = lazy(
	() => import('./pages/repositories/RepositoriesDetail'),
);
const Settings = lazy(() => import('./pages/settings/Settings'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const PageLayout = lazy(() => import('./PageLayout'));
const AdminPanel = lazy(() => import('./pages/admin/Admin'));
const AdminRouteGuard = lazy(() => import('./components/RequireAdmin'));
const Repositories = lazy(() => import('./pages/repositories/Repositories'));
const BranchDetailsPage = lazy(() => import('./pages/branches/Branches'));
const AssignedToMe = lazy(() => import('./pages/assignedToMe/AssignedToMe'));
const Onboarding = lazy(() => import('./pages/onboarding/Onboarding'));
const OnboardingRouteGuard = lazy(
	() => import('./components/RequireOnboarding'),
);
const AccessDenied = lazy(() => import('./pages/accessDenied/AccessDenied'));

import {
	AuthenticatedRoute,
	ForgotPassword,
	ResetPassword,
	ChangePassword,
} from '@sal/portal';
import { useAuth } from '@sal/authentication.react';
import { useBrandContext } from 'code/context/BrandContext';
const SsoLoginPage = lazy(() => import('./pages/SsoLoginPage'));
const SalCoreLoginPage = lazy(() => import('./pages/SalCoreLoginPage'));
const SsoCallbackPage = lazy(() => import('./pages/SsoCallbackPage'));

export default function Routes() {
	const auth = useAuth();
	const { setBrandId } = useBrandContext();

	// this is assuming that the user will have companyId set on login in the future, if not default to sandfield theme
	useEffect(() => {
		if (auth.isLoggedIn && auth.user?.companyId) {
			setBrandId(auth.user.companyId);
		}
	}, [auth.isLoggedIn, auth.user?.companyId, setBrandId]);

	return (
		<Router>
			<ErrorBoundary fallback={<ErrorPage />}>
				<ReactRouterRoutes>
					<Route element={<PageLayout />}>
						<Route element={<AuthenticatedRoute />}>
							<Route
								path="/Onboarding"
								element={<Onboarding />}
							/>

							{/* Admins must be able to reach the Admin Panel even if they
								haven't completed onboarding themselves (e.g. to assign
								teams to other users), so this sits outside the onboarding gate. */}
							<Route
								path="/admin"
								element={
									<AdminRouteGuard>
										<AdminPanel />
									</AdminRouteGuard>
								}
							/>

							<Route element={<OnboardingRouteGuard />}>
								<Route path="/" element={<Dashboard />} />

								{/* Alert-gated pages */}
								<Route
									element={
										<RequirePermission
											requirements={[
												{
													securityContext: 'Alert',
													permission: 'ReadAlerts',
												},
											]}
										/>
									}
								>
									<Route
										path="/Alerts"
										element={<Alerts />}
									/>
									<Route
										path="/Alerts/:id"
										element={<AlertsDetail />}
									/>
									<Route
										path="/AssignedToMe"
										element={<AssignedToMe />}
									/>
								</Route>

								{/* Repository-gated pages */}
								<Route
									element={
										<RequirePermission
											requirements={[
												{
													securityContext:
														'Repository',
													permission:
														'ReadRepositories',
												},
											]}
										/>
									}
								>
									<Route
										path="/Repositories"
										element={<Repositories />}
									/>
									<Route
										path="/Repositories/:id"
										element={<RepositoryDetail />}
									/>
									<Route
										path="/Subscriptions"
										element={<Subscriptions />}
									/>
								</Route>

								{/* Branch-gated pages */}
								<Route
									element={
										<RequirePermission
											requirements={[
												{
													securityContext: 'Branch',
													permission: 'ReadBranches',
												},
											]}
										/>
									}
								>
									<Route
										path="/Repositories/:repoId/Branches/:branchId"
										element={<BranchDetailsPage />}
									/>
								</Route>

								{/* Settings-gated pages */}
								<Route
									element={
										<RequirePermission
											requirements={[
												{
													securityContext: 'Settings',
													permission: 'ReadSettings',
												},
											]}
										/>
									}
								>
									<Route
										path="/Settings"
										element={<Settings />}
									/>
								</Route>
							</Route>
						</Route>
					</Route>

					{/* PORTAL FRAMEWORK PAGES*/}
					<Route path="/Login" element={<SsoLoginPage />} />
					<Route path="/AccessDenied" element={<AccessDenied />} />
					<Route
						path="/SalCoreLogin"
						element={<SalCoreLoginPage />}
					/>
					<Route path="/SsoCallback" element={<SsoCallbackPage />} />
					<Route
						path="/ForgotPassword"
						element={<ForgotPassword />}
					/>
					<Route
						path="/ChangePassword"
						element={<ChangePassword />}
					/>
					<Route
						path="/ResetPassword/:passwordResetToken"
						element={<ResetPassword />}
					/>
					<Route path="/NotFound" element={<NotFound />} />
					<Route path="*" element={<NotFound />} />
					{/* /PORTAL FRAMEWORK PAGES*/}
				</ReactRouterRoutes>
			</ErrorBoundary>
		</Router>
	);
}
